import React, { useState, useEffect } from 'react';
import { groceryAPI } from '../services/api';
import { GroceryItem } from '../types';
import { CATEGORIES } from '../utils/constants';
import { Card } from './common/Card';
import { Button } from './common/Button';
import { Badge } from './common/Badge';
import { Input } from './common/Input';
import { Select } from './common/Select';
import { Stack } from './layout/Stack';
import GroceryItemModal from './GroceryItemModal';

interface GroceryItemTableProps {
  listId: string | null;
  onItemsChanged?: () => void;
}

const GroceryItemTable: React.FC<GroceryItemTableProps> = ({ listId, onItemsChanged }) => {
  const [items, setItems] = useState<GroceryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<GroceryItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId]);

  const fetchItems = async () => {
    if (!listId) { setItems([]); setLoading(false); return; }
    try {
      setLoading(true);
      const response = await groceryAPI.getAll(listId);
      if (response.success && response.data) {
        setItems(response.data);
      }
    } catch (err) {
      setError('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: GroceryItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await groceryAPI.delete(id);
      await fetchItems();
      onItemsChanged?.();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const handleModalDelete = async () => {
    if (!editingItem?._id) return;
    await handleDelete(editingItem._id);
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    await fetchItems();
    onItemsChanged?.();
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = !searchQuery || item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || item.type === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const categoryOptions = [
    { value: '', label: 'Toutes les catégories' },
    ...CATEGORIES.map(c => ({ value: c.name, label: `${c.emoji} ${c.name}` })),
  ];

  if (loading) {
    return <div className="text-center text-gray-500 py-6">Chargement des articles...</div>;
  }

  return (
    <div>
      {error && (
        <div className="bg-red-50 text-danger border-l-4 border-danger px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Search, filter, add bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input
          placeholder="Rechercher un article..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          fullWidth={false}
          className="sm:w-56"
        />
        <Select
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          options={categoryOptions}
          fullWidth={false}
          className="sm:w-52"
        />
        <Button type="button" variant="primary" size="md" onClick={handleAdd}>
          + Nouvel article
        </Button>
      </div>

      <p className="text-sm text-gray-500 mb-3">{filteredItems.length} article{filteredItems.length !== 1 ? 's' : ''}</p>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-gray-700 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 font-semibold">Article</th>
              <th className="px-4 py-3 font-semibold">Catégorie</th>
              <th className="px-4 py-3 font-semibold">Qté</th>
              <th className="px-4 py-3 font-semibold">Statut</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  Aucun article trouvé
                </td>
              </tr>
            ) : (
              filteredItems.map(item => (
                <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {item.emoji} {item.name}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="info" size="sm">{item.type}</Badge>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{item.quantity}</td>
                  <td className="px-4 py-3">
                    {item.bought
                      ? <Badge variant="success" size="sm">Acheté</Badge>
                      : <Badge variant="warning" size="sm">À acheter</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => handleEdit(item)}>
                        Modifier
                      </Button>
                      <Button type="button" variant="danger" size="sm" onClick={() => handleDelete(item._id!)}>
                        Supprimer
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden">
        {filteredItems.length === 0 ? (
          <p className="text-center text-gray-500 py-8">Aucun article trouvé</p>
        ) : (
          <Stack direction="vertical" spacing={2}>
            {filteredItems.map(item => (
              <Card key={item._id} padding="md" shadow="soft">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{item.emoji} {item.name}</span>
                  <Badge variant={item.bought ? 'success' : 'warning'} size="sm">
                    {item.bought ? 'Acheté' : 'À acheter'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{item.type} · Qté : {item.quantity}</span>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => handleEdit(item)}>
                      ✏️
                    </Button>
                    <Button type="button" variant="danger" size="sm" onClick={() => handleDelete(item._id!)}>
                      🗑️
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </Stack>
        )}
      </div>

      <GroceryItemModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingItem(null); }}
        onSave={handleSave}
        listId={listId}
        itemToEdit={editingItem}
        onDelete={editingItem ? handleModalDelete : undefined}
      />
    </div>
  );
};

export default GroceryItemTable;
